import React, { useEffect, useCallback, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  ButtonCancel,
  toastFailure,
  YupObject,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  gqlGetByIdQuery,
  gqlAdd,
  gqlUpdate,
  gqlDelete,
  FormInput,
  ContributionItemInput,
  CntContributionItemDetailInput,
} from '../graphql';
import { FormFields } from './contribution-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';

import { convertFieldToFloat, convertFieldToInt } from 'shared-pages/common/form-helpfunc';
import { isRebattery, requiredString } from 'shared-pages/common/constants';
import { getRouteByName } from 'app/routes';
import { ContributionSeries, ContributionStates } from 'shared-pages';
import { FaFilePdf } from 'react-icons/fa';
import { Button, Spinner } from 'react-bootstrap';
import { FaSave, FaTrash, FaPlus } from 'react-icons/fa';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { FormatDate } from 'shared-pages/common/date-helpfunc';
import { formatString } from 'shared-pages/common/string-helpfunc';

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  paramCollectorId?: number | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const ContributionFormPage = ({
  isModal = false,
  paramId = undefined,
  paramCollectorId,
  modalOnClose,
}: Props) => {
  const { handleGraphqlError } = useHandleError('driver');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location = useLocation<any>();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t, i18n } = useTranslation();

  const formShape = {
    id: YupNumeric,
    legacyAppId: YupString,
    statusId: YupNumeric,
    series: YupObject,
    contributor: YupObject,
    contributionperiod: YupObject,
    contributionstate: YupObject,
    submittedByUser: YupString,
    declarationDate: YupString,
    periodyear: YupString, // calculated
    isInvoiced: YupBoolean,
    releaseToErp: YupBoolean,
    isCanceled: YupBoolean,
    invoiceFileId: YupNumeric,
    invoiceFile: YupObject.nullable(),
    creditInvoiceFileId: YupNumeric,
    creditInvoiceFile: YupObject.nullable(),
    totalCost: YupNumeric,
    totalCostWithVat: YupNumeric,
    totalQty: YupNumeric,
    totalWeight: YupNumeric,
    userStringField1: YupString,
    userStringField2: YupString,
    userStringField3: YupString,
    userStringField4: YupString,
    userStringField5: YupString,
    userStringField6: YupString,
    userStringField7: YupString,
    userStringField8: YupString,
    userStringField9: YupString,
    userStringField10: YupString,
    contributionItems: Yup.array().of(
      Yup.object().shape(
        {
          id: YupNumeric,
          aa: YupNumeric,
          itemId: YupNumeric,
          qty1: Yup.number()
            .nullable()
            /* .typeError('Η τιμή πρέπει να είναι αριθμός.')
            .positive('Η τιμή πρέπει να είναι θετικός αριθμός.')
            .integer(t('Η τιμή πρέπει να είναι ακέραιος αριθμός.')) */
            .when('qty2', {
              is: (qty2) => {
                return qty2 && parseFloat(qty2) > 0;
              },
              then: YupNumeric.required(t(requiredString)),
            }),
          qty2: YupNumeric
            /* .typeError('Η τιμή πρέπει να είναι αριθμός.')
            .positive('Η τιμή πρέπει να είναι θετικός αριθμός.')
            .test('is-decimal', 'Η τιμή πρέπει να έχει έως δύο δεκαδικά.', (val: any) => {
              let patternTwoDigisAfterComma = new RegExp(/^(|\d)+(\.\d{0,2})?$/);
              if (val != undefined) {
                return patternTwoDigisAfterComma.test(val);
              }
              return true;
            }) */
            .when('qty1', {
              is: (qty1) => {
                return qty1 && parseFloat(qty1) > 0;
              },
              then: YupNumeric.required(t(requiredString)),
            }),
          qty3: YupNumeric,
          /* .typeError('Η τιμή πρέπει να είναι αριθμός.')
            .positive(
            'Η τιμή πρέπει να είναι θετικός αριθμός.',
          ) */ price: YupNumeric,
          item: YupObject,
          unitPrice: YupObject,
        },
        [['qty1', 'qty2']],
      ),
    ),
    subTotals: YupObject,
    totals: YupObject,
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch: refetchQuery, loading }] =
    useLazyQuery(gqlGetByIdQuery);
  // const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [data, setData] = useState<any>();

  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  const isFormEditable = useCallback(() => {
    //disabled={data?.statusId === ContributionStates.Saved || appStore.isAfis}
    if (appStore.isContributor) {
      if (
        data &&
        (data.statusId === ContributionStates.Empty || data.statusId === ContributionStates.Draft)
      ) {
        const eYear = data.year + data.contributionperiod.periodEndYearAdd;
        const eMonth = data.contributionperiod.periodEndMonth;
        const eDay = data.contributionperiod.periodEndDay;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eDate = new Date(eYear, eMonth - 1, eDay); // month is zero based
        // Εξαίρεση για Τέλος Έτους
        if (eMonth === 12 && eDay === 31) {
          eDate.setDate(eDate.getDate() - 15); // -15 ημέρες
        }
        // Επέτρεψε 1 εβδομάδα πρίν την τελευταία ημέρα της περιόδου
        const oneWeekBeforeEndDate = new Date(eDate);
        oneWeekBeforeEndDate.setDate(eDate.getDate() - 7);
        if (today >= oneWeekBeforeEndDate) return true;
        //if (today >= eDate) return true;
      }
    }
    return false;
  }, [appStore.isContributor, data]);

  // Μήνυμα Κλειδώματος Εισφοράς
  const [editMessage, setEditMessage] = useState('');
  useEffect(() => {
    if (data && appStore.isContributor) {
      if (data.statusId === ContributionStates.Saved) {
        setEditMessage(t('Η Δήλωση έχει υποβληθεί. Δεν επιτρέπεται η μεταβολή της.'));
      } else if (
        data.statusId === ContributionStates.Empty ||
        data.statusId === ContributionStates.Draft
      ) {
        const eYear = data.year + data.contributionperiod.periodEndYearAdd;
        const eMonth = data.contributionperiod.periodEndMonth;
        const eDay = data.contributionperiod.periodEndDay;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eDate = new Date(eYear, eMonth - 1, eDay); // month is zero based
        // Εξαίρεση για Τέλος Έτους
        if (eMonth === 12 && eDay === 31) {
          eDate.setDate(eDate.getDate() - 15); // -15 ημέρες
        }

        // Επέτρεψε 1 εβδομάδα πρίν την τελευταία ημέρα της περιόδου
        const oneWeekBeforeEndDate = new Date(eDate);
        oneWeekBeforeEndDate.setDate(eDate.getDate() - 7);
        if (today < oneWeekBeforeEndDate) {
          setEditMessage(
            `${t('Η Δήλωση θα είναι διαθέσιμη για υποβολή μετά τις')} ${FormatDate(
              oneWeekBeforeEndDate,
            )}`,
          );
        }

        // if (today < eDate) {
        //   setEditMessage(
        //     `${t('Η Δήλωση θα είναι διαθέσιμη για υποβολή μετά τις')} ${FormatDate(eDate)}`,
        //   );
        // }
      }
    }
    // Το μήνυμα αποθηκεύεται σε state· χωρίς τη γλώσσα μένει ελληνικό μετά το EN
  }, [appStore.isContributor, data, i18n.language]);

  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (isNew) {
      // initialize new record
      initializeNewRecord();
    } else {
      // fetch data
      get({ variables: { id: idNum } })
        .then((value) => {
          if (!value?.data) return;
          // Υπολόγισε Μερικά Αθροίσματα ανα Πίνακα
          const data = value.data[Object.keys(value.data)[0]];
          if (!data) return;
          const items = data.contributionItems ?? [];
          const subTotals: any = {};
          items.map((item, idx, arr) => {
            const groupId = item.item.groupId;
            if (!subTotals[`group${groupId}`]) {
              subTotals[`group${groupId}`] = { qty1: 0, qty2: 0, price: 0 };
            }
            if (item.qty1)
              subTotals[`group${groupId}`].qty1 =
                subTotals[`group${groupId}`].qty1 + parseFloat(item.qty1);
            if (item.qty2)
              subTotals[`group${groupId}`].qty2 =
                subTotals[`group${groupId}`].qty2 + parseFloat(item.qty2);
            if (item.price)
              subTotals[`group${groupId}`].price =
                subTotals[`group${groupId}`].price + parseFloat(item.price);
          });
          // Σύνθεσε Περίοδο Εισφορών
          // const periodyear = `${data?.contributionperiod?.name} ${data?.year}`;
          const periodyear = `${t(data?.contributionperiod?.name)} ${data?.year}`;
          const newData = {
            ...data,
            subTotals,
            periodyear: periodyear,
            contributionperiod: {
              ...data.contributionperiod,
              name: t(data.contributionperiod.name),
            },
            series: { ...data.series, name: t(data.series.name) },
            contributionstate: { ...data.contributionstate, name: t(data.contributionstate.name) },
          };
          // newData.series.name = t(newData.series.name);
          setData(newData);
        })
        .catch((err) => handleGraphqlError('query-get', err));
    }
    // Η περίοδος και η κατάσταση μεταφράζονται εδώ από το ελληνικό όνομα της βάσης
  }, [get, id, idNum, i18n.language]);

  /** Αρχικοποίησε Νέα Εγγραφή  */
  const initializeNewRecord = async () => {
    let newRec: any = { isActive: true };
    if (appStore.isCollector) {
      newRec = {
        ...newRec,
        collectorId: appStore.associatedId,
      };
    } else if (paramCollectorId) {
      newRec = {
        ...newRec,
        collectorId: paramCollectorId,
      };
    }
    setInitialNewRecData(newRec);
  };

  /*-- Εκτύπωση Εισφοράς σε Pdf --*/
  const printPdf = () => {
    const url = process.env.VITE_APP_WEBAPI.replace('/graphql', `/cnt/${id}/${i18n.language}`);
    //window.open(url, '_system', 'location=yes');
    window.open(url, '_blank').focus();
  };
  const refetch = async () => {
    const ret = await refetchQuery();
    const id = ret.data[Object.keys(ret.data)[0]].id;
    history.replace({
      pathname: generatePath(getRouteByName('Contribution'), { id: id }),
      state: {
        pathname: location.pathname,
        ...location.state,
      },
    });
  };

  const navigateToContribution = async (contributionId: number) => {
    history.push({
      pathname: generatePath(getRouteByName('Contribution'), { id: contributionId }),
      state: {
        pathname: location.pathname,
        ...location.state,
      },
    });
  };

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    console.log('handleSubmit invoked ', values);
    actions.setSubmitting(false);

    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

    convertFieldToFloat(mutValues, ['totalCost', 'totalQty', 'totalWeight']);

    // contributionItems
    (mutValues as any).contributionItems = (mutValues as any).contributionItems.map((values) => {
      const details = values.cntContributionItemDetails;
      const newValues = { ...values };

      convertFieldToFloat(newValues, ['qty2', 'price']);
      convertFieldToInt(newValues, ['qty1']);
      const mutValues = Lo.pick(newValues, Lo.keys(new ContributionItemInput()));

      // Details
      if (details && details.length > 0) {
        const mutDetails = details.map((detail) => {
          const newDetail = { ...detail };
          convertFieldToFloat(newDetail, ['qty2', 'price', 'qty3']);
          convertFieldToInt(newDetail, ['aa', 'qty1']);
          const mutDetail = Lo.pick(newDetail, Lo.keys(new CntContributionItemDetailInput()));
          return mutDetail;
        });
        mutValues.cntContributionItemDetails = mutDetails;
      }

      return mutValues;
    });

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      // ΔΕΝ ΕΙΝΑΙ ΠΟΤΕ ΝΕΑ ΕΓΓΡΑΦΗ
      return;
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then(async (d) => {
          toastSuccess(
            `${t('Επιτυχής Ενημέρωση Εγγραφής')} [${d.data[Object.keys(d.data)[0]].id}]`,
          );
          await refetch();
          const url =
            process.env.REACT_APP_CUSTOM_NODE_ENV == 'amazonpro'
              ? 'https://yy3t9ondbd.execute-api.eu-west-1.amazonaws.com/pro-re-battery'
              : 'https://x9caq5to5i.execute-api.eu-west-1.amazonaws.com/dev-re-battery';
          fetch(`${url}/triggermailservice`);
          // const id = d.data[Object.keys(d.data)[0]].id;
          // history.push({
          //   pathname: generatePath(getRouteByName('Contribution'), { id: id }),
          //   state: {
          //     pathname: location.pathname,
          //     ...location.state,
          //   },
          // });
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: t('Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;'),
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  const gqlCancelContribution = gql`
    mutation cancelContribution($id: Int!) {
      cancelContribution(id: $id)
    }
  `;
  const [cancelContributionMutation] = useMutation(gqlCancelContribution);

  const gqlAddComlementaryContribution = gql`
    mutation addComplementaryContribution($contributorId: Int!, $year: Int!, $periodId: Int!) {
      addComplementaryContribution(contributorId: $contributorId, year: $year, periodId: $periodId)
    }
  `;
  const [addComlementaryContributionMutation] = useMutation(gqlAddComlementaryContribution);
  const gqlCancelAndAddCorrectiveContributionMutation = gql`
    mutation cancelAndAddCorrectiveContributionMutation($id: Int!) {
      cancelAndAddCorrectiveContributionMutation(id: $id)
    }
  `;
  const [cancelAndAddCorrectiveContributionMutation] = useMutation(
    gqlCancelAndAddCorrectiveContributionMutation,
  );

  /* ***********************************************************************
     Custom Header Buttons
     *********************************************************************** */

  const HeaderButtons = () => {
    const disabled = false;
    // Action: Δημιουργία Συμπληρωματικής Δήλωσης
    const showAddComplementaryContribution =
      //appStore.isPlatformOwner && // Platform Owner
      !data?.isCanceled && // & Δεν έχει Ακυρωθεί
      data?.statusId === ContributionStates.Saved && // & Είναι Οριστικοποιημένη
      ((data?.releaseToErp && // & Είναι Διαθέσιμη στο ERP
        data?.isInvoiced && // & Έχει Τιμολογηθεί
        appStore.isPlatformOwner) ||
        appStore.isContributor) &&
      [ContributionSeries.Initial, ContributionSeries.Corrective].includes(data?.seriesId); // & Αρχική ή Διορθωτική

    // Action: Ακυρωση & Δημιουργία Διορθωτικής
    const showCancelAndAddCorrectiveContribution =
      //appStore.isPlatformOwner && // Platform Owner
      !data?.isCanceled && // & Δεν έχει Ακυρωθεί
      data?.statusId === ContributionStates.Saved && // & Είναι Οριστικοποιημένη
      data?.releaseToErp && // & Είναι Διαθέσιμη στο ERP
      data?.isInvoiced && // & Έχει Τιμολογηθεί
      [ContributionSeries.Initial, ContributionSeries.Corrective].includes(data?.seriesId); // & Αρχική ή Διορθωτική

    async function confirmDialog(text: string): Promise<boolean> {
      return await promiseModalDialog({
        title: t('Επιβεβαίωση'),
        text,
      });
    }

    // Ακύρωση και Δημιουργία Διορθωτικής Δήλωσης
    async function cancelAndAddCorrectiveContribution() {
      // Δημιουργία Διορθωτικής Δήλωσης
      const res = await confirmDialog(
        t('Θέλετε να δημιουργηθεί νέα διορθωτική δήλωση για την ίδια περίοδο;'),
      );
      if (!res) return;
      const vars = {
        id: data?.id,
      };
      cancelAndAddCorrectiveContributionMutation({ variables: vars })
        .then((d) => {
          toastSuccess(
            `${t('Δημιουργήθηκε Νέα Δήλωση με Αριθμό:')} ${
              (d as any).data.addComlementaryContribution
            }`,
          );
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-cancelAndAddCorrectiveContribution', error);
        });
    }

    // Δημιουργία Συμπληρωματικής Δήλωσης

    async function addComplementaryContribution() {
      // Δημιουργία Συμπληρωματικής Δήλωσης
      const res = await confirmDialog(
        t('Θέλετε να δημιουργηθεί νέα συμπληρωματική δήλωση για την ίδια περίοδο;'),
      );
      if (!res) return;
      const vars = {
        contributorId: data?.contributorId,
        year: data?.year,
        periodId: data?.periodId,
        contributionBatteriesGroupId: data?.contributionBatteriesGroupId,
      };
      addComlementaryContributionMutation({ variables: vars })
        .then((d) => {
          const contributionId = (d as any).data.addComplementaryContribution;
          toastSuccess(`${t('Δημιουργήθηκε Νέα Δήλωση με Αριθμό:')} ${contributionId}`);
          navigateToContribution(contributionId);
        })
        .catch((error) => {
          handleGraphqlError('mutation-addComlementaryContribution', error);
        });
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        {/* Ακύρωση και Δημιουργία Διορθωτικής Δήλωσης
            ------------------------------------------  */}
        <Button
          hidden={!showCancelAndAddCorrectiveContribution}
          disabled={loading || disabled}
          variant="warning"
          onClick={cancelAndAddCorrectiveContribution}
          style={{ marginRight: '0.8rem' }}
        >
          <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
          <FaPlus className="mb-1" /> {t('Ακύρωση και Δημιουργία Διορθωτικής Δήλωσης')}
        </Button>

        {/* Δημιουργία Συμπληρωματικής Δήλωσης
              ----------------------------------  */}
        <Button
          hidden={!showAddComplementaryContribution}
          disabled={loading || disabled}
          variant="warning"
          onClick={addComplementaryContribution}
          style={{ marginRight: '0.8rem' }}
        >
          <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
          <FaPlus className="mb-1" /> {t('Δημιουργία Συμπληρωματικής Δήλωσης')}
        </Button>
      </div>
    );
  };

  /* ***********************************************************************
     Custom Form Footer Buttons
     *********************************************************************** */
  const footerButtons = (formikCtx: any) => {
    //
    const { dirty, resetForm, isValid, loading, errors } = formikCtx;

    /* Υπολόγισε ποιά actions είναι visible */
    /*
      | actions                                  | Περιγραφή                                                                            |
      | ---------------------------------------- | ------------------------------------------------------------------------------------ |
      | Προσωρινή Αποθήκευση                     | Αποθήκευση απο τον Εισφορέα                                                          |
      | Υποβολή                                  | Υποβολή απο τον Εισφορέα και αποστολή για τιμολόγηση απο τον Διαχειριστή             |
      | Επαναφορά σε Προσωρινά Αποθηκευμένη      | Ο Διαχειριστής την επαναφέρει προς επεξεργασία απο τον Εισφορέα, εφόσον δεν έχει τιμ |
      | Δημιουργία Συμπληρωματικής Δήλωσης       | Ο Διαχειριστής δημιουργεί για την ίδια περίοδο μία πρόσθετη δήλωση                   |
      | Ακύρωση & Δημιουργία Διορθωτικής Δήλωσης | Ακύρωση τρέχουσας - Αρχικής Δήλωσης και δημιουργία νέας Δοωρθωτικής Δήλωσης          |
      | Ακύρωση                                  | Ακύρωση για Συμπληρωματική Δήλωση                                                    |

      | seriesId             | statusId        | userRole     | isInvoiced | releaseToErp | isCanceled | actions                                          |
      | -------------------- | --------------- | ------------ | ---------- | ------------ | ---------- | ------------------------------------------------ |
      | Αρχικη Δήλ., Διορθ.Δ | Νέα Εγγραφή     | Εισφορέας    | οχι        | οχι          | οχι        | Προσωρινή Αποθ., Υποβολή                         |
      | Αρχικη Δήλ., Διορθ.Δ | Προσωρινή Αποθ. | Εισφορέας    | οχι        | οχι          | οχι        | Υποβολή                                          |
      | Αρχικη Δήλ., Διορθ.Δ | Έχει Υποβληθεί  | Διαχειριστής | οχι        | οχι          | οχι        | Επαναφορά σε Προσωρινά Αποθηκευμένη              |
      | Αρχικη Δήλ., Διορθ.Δ | Έχει Υποβληθεί  | Διαχειριστής | ναι        | ναι          | οχι        | Ακυρ. & Δημ. Διορθωτικής, Δημ. Συμπληρωματικής Δ |
      | Διορθωτική Δήλ.      | Έχει Υποβληθεί  | Εισφ/Διαχειρ | ναι        | ναι          | οχι        | Ακυρωση & Δημιουργία Διορθωτικής                 |
      | Συμπληρωματική Δήλ.  | Έχει Υποβληθεί  | Διαχειριστής | ναι        | ναι          | οχι        | Ακύρωση                                          |
    */

    // Action: Προσωρινή Αποθήκευση
    const showTempStorage =
      appStore.isContributor &&
      (data?.statusId === ContributionStates.Empty || data?.statusId === ContributionStates.Draft);

    // Action: Υποβολή
    //const showFinilize = appStore.isContributor && data?.statusId === ContributionStates.Draft;  // Προηγείται της Υποβολής η Προσωρινή Αποθήκευση
    const showFinalize =
      appStore.isContributor &&
      isFormEditable() &&
      (data?.statusId === ContributionStates.Empty || data?.statusId === ContributionStates.Draft); // Υποβολή Χωρίς Προσωρινή Αποθήκευση

    // Action: Επαναφορά σε Προσωρινά Αποθηκευμένη
    const showChangeToTempStored =
      appStore.isPlatformOwner && // Platform Owner
      !data?.isCanceled && // & Δεν έχει Ακυρωθεί
      data?.statusId === ContributionStates.Saved && // & Είναι Οριστικοποιημένη
      !data?.releaseToErp && // & ΔΕΝ Είναι Διαθέσιμη στο ERP
      !data?.isInvoiced; // & Δεν έχει Τιμολογηθεί

    // Action: Ακύρωση (Διαγραφή) Εισφοράς
    const showCancelContribution =
      appStore.isPlatformOwner && // Platform Owner
      !data?.isCanceled && // & Δεν έχει Ακυρωθεί
      // data?.statusId === ContributionStates.Saved && // & Είναι Οριστικοποιημένη
      // data?.releaseToErp && // & Είναι Διαθέσιμη στο ERP
      // !data?.isInvoiced && // & Δεν έχει Τιμολογηθεί
      [ContributionSeries.Complementary].includes(data?.seriesId); // & Συμπληρωματική

    const disabled = false;
    //console.log(formikCtx);
    if (!data) return <>No Data</>;

    async function confirmDialog(text: string): Promise<boolean> {
      return await promiseModalDialog({
        title: 'Επιβεβαίωση',
        text,
      });
    }
    async function tempStorageForm() {
      const newValues = { ...formikCtx.values, statusId: ContributionStates.Draft };
      console.log('newValues', newValues);
      await handleSubmit(newValues, { setSubmitting: formikCtx.setSubmitting });
    }

    async function changeToStored() {
      const res = await confirmDialog(
        t('Η Εισφορά θα αλλάξει Κατάσταση, σε Προσωρινά Αποθηκευμένη.'),
      );
      if (res) {
        appStore.setBusy();
        const res = await updateRecord({
          variables: {
            data: { statusId: 2, submittedByUser: null, declarationDate: null, id: idNum },
          },
        });
        if (res) {
          toastSuccess(
            `${t('Επιτυχής Αλλαγή Εισφοράς')} [${
              res.data[Object.keys(res.data)[0]].id
            }] ${t('Προσωρινά Αποθηκευμένη')}`,
          );
          appStore.clearBusy();
          refetch();
        }
      }
    }

    async function finalizeForm() {
      let res = false;

      if (!isValid) {
        toastFailure(t('Υπάρχουν λάθη στα πεδία της φόρμας'));
        formikCtx.handleSubmit(); // Για να δείξει τα λάθη
        return;
      }

      // Μηδενική Δήλωση
      if (!formikCtx.values.totalCost || parseFloat(formikCtx.values.totalCost) === 0) {
        res = await confirmDialog(
          t('Προσοχή η δήλωση είναι μηδενική. Θέλετε να υποβάλετε Μηδενική δήλωση;'),
        );
        if (res) {
          formikCtx.values.totalCost = '0';
          formikCtx.values.totalQty = '0';
          formikCtx.values.totalWeight = '0';
        }
      } else {
        res = await confirmDialog(
          t('Θέλετε να Οριστικοποιηθεί η Δήλωση και να σταλεί για τιμολόγηση;'),
        );
      }
      if (res) {
        appStore.setBusy();
        formikCtx.setFieldValue('statusId', ContributionStates.Saved);
        formikCtx.setFieldValue('submittedByUser', appStore.user);
        formikCtx.setFieldValue('declarationDate', new Date());

        await formikCtx.handleSubmit();

        //refetch();
        appStore.clearBusy();
      }
    }

    async function saveForm() {
      const newValues = { ...formikCtx.values };
      await handleSubmit(newValues, { setSubmitting: formikCtx.setSubmitting });
    }

    async function cancelContribution() {
      // Ακύρωση Δήλωσης και δημιουργία νέας
      const res = await confirmDialog(t('Θέλετε να ακυρωθεί η δήλωση;'));
      if (!res) return;
      cancelContributionMutation({ variables: { id: data?.id } })
        .then((d) => {
          toastSuccess(
            formatString('Ακυρώθηκε η Δήλωση με Αριθμό: {0}', (d as any).data.cancelContribution),
          );
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-cancelContribution', error);
        });
    }

    return (
      <div>
        {!isValid && errors && (
          <h3 className="text-danger">
            {formatString('Υπάρχουν {0} λάθη στα στοιχεία της φόρμας', Object.keys(errors).length)}
          </h3>
        )}
        {/* Προσωρινή Αποθήκευση
            ----------------------------------- */}
        {!isRebattery && (
          <Button
            hidden={!showTempStorage}
            disabled={!dirty || loading || disabled || (dirty && !isValid)}
            variant="primary"
            onClick={tempStorageForm}
            style={{ marginRight: '0.8rem' }}
          >
            <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
            <FaSave className="mb-1" /> {t('Προσωρινή Αποθήκευση')}
          </Button>
        )}

        {/* Οριστικοποίηση - Υποβολή
            ----------------------------------- */}
        <Button
          hidden={!showFinalize}
          disabled={loading || disabled} // || (dirty && !isValid)}
          variant="success"
          //type="submit"
          onClick={finalizeForm}
          style={{ marginRight: '0.8rem' }}
        >
          <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
          <FaSave className="mb-1" /> {t('Υποβολή Δήλωσης')}
        </Button>

        {/* Ακύρωση Δήλωσης
            ----------------------------------- */}
        <Button
          hidden={!showCancelContribution}
          disabled={dirty || loading || disabled}
          variant="danger"
          onClick={cancelContribution}
          style={{ marginRight: '0.8rem' }}
        >
          <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
          <FaTrash className="mb-1" /> {t('Ακύρωση Δήλωσης Εισφοράς')}
        </Button>

        {/* Επαναφορά σε Προσωρινά Αποθηκευμένη
            ----------------------------------- */}
        <Button
          hidden={!showChangeToTempStored}
          disabled={dirty || loading || disabled}
          variant="warning"
          onClick={changeToStored}
          style={{ marginRight: '0.8rem' }}
        >
          <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
          <FaSave className="mb-1" /> {t('Επαναφορά Σε Προσωρινά Αποθηκευμένη')}
        </Button>

        {/* Αποθήκευση
            ----------------------------------- */}
        <Button
          hidden={!(appStore.isPlatformOwner && dirty)}
          disabled={!dirty || loading || disabled}
          // text="Αποθήκευση"
          // loading={loading}
          onClick={saveForm}
          style={{ marginRight: '0.8rem', minWidth: '160px' }}
        >
          <Spinner hidden={!loading} animation="border" size="sm" className="mb-1" />
          <FaSave className="mb-1" /> {t('Αποθήκευση')}
        </Button>

        {/* Ακύρωση
            ----------------------------------- */}
        <span style={{ marginRight: '0.8rem', display: !dirty ? 'none' : 'initial' }}>
          <ButtonCancel
            hidden={!dirty}
            disabled={!dirty || loading || disabled}
            onClick={resetForm}
          />
        </span>

        {/* Εκτύπωση
            ----------------------------------- */}
        <Button
          style={{ marginRight: '0.8rem' }}
          disabled={dirty || loading}
          variant="success"
          onClick={() => printPdf()}
        >
          <FaFilePdf className="mb-1" /> {t('Εκτύπωση Δήλωσης')}
        </Button>
      </div>
    );
  };

  /**
   * * Render
   */
  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <div className="d-flex flex-column h-100">
        <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
          <FormCard
            title={
              <span>
                {`${t('Εισφορά')}: ${data?.contributionperiod?.name ?? ''} ${
                  !isRebattery ? data?.contributionperiod?.months ?? '' : ''
                }  ${data?.year ?? ''} [${data?.contributionstate.name}] `}
                <strong className="text-warning">{editMessage}</strong>
              </span>
            }
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onReturn={null}
            permitSubmit={false}
            permitDelete={false}
            initialValues={isNew ? initialNewRecData : pickProperties(data)}
            validationSchema={validationSchema}
            enableReinitialize
            idx={idNum}
            injectFooterElements={footerButtons}
            noButtons={true}
          >
            <HeaderButtons />
            <FormFields
              isNew={isNew}
              updateRecord={updateRecord}
              refetch={refetch}
              data={data}
              isFormEditable={isFormEditable()}
            />
            {appStore.isPlatformOwner && <RowFieldTooltips data={data} showLegacy={false} />}
          </FormCard>
        </LoadingOverlay>
      </div>
    );
  }
};
